import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "p-4 space-y-3" }
const _hoisted_2 = { class: "flex justify-between items-end space-x-3" }
const _hoisted_3 = { class: "flex-shrink-0" }
const _hoisted_4 = { class: "flex justify-between items-end space-x-3" }
const _hoisted_5 = { class: "flex-shrink-0" }
const _hoisted_6 = { class: "flex justify-between space-x-3" }
const _hoisted_7 = { class: "flex justify-between space-x-3" }
const _hoisted_8 = { class: "flex justify-between space-x-3" }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("div", { class: "w-1/2" }, null, -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("hr", null, null, -1)
const _hoisted_11 = { class: "flex justify-end space-x-3" }

import { computed, ref, watch } from 'vue';

import { CarResource } from '@shared/resources/car';
import { CarCreateUpdateDto } from '@shared/dto/car';
import { useEmitter } from '@/plugins/mitt/mitt';
import { CarService } from '@/services/car.service';
import { useCarModelsStore } from '@/stores/dictionary/car-models';

import { BaseModal, useModalsStore, useModalControls } from '@/components/modals';
import { ClientResource } from '@shared/resources/client';
import { EngineFuel, EngineFuelList, EngineFuelNameMap } from '@shared/types/engine-fuel';
import { carCreateUpdateSchema } from '@shared/validation/car';
import ImageUpload from '@ui-kit/inputs/image-upload/ImageUpload.vue';
import { SingleSelect, useSelectClientDataManager } from '@ui-kit/inputs/select';
import Label from '@ui-kit/label/Label.vue';
import { CarModelResource } from '@shared/resources/car-model';
import { ValidationKeyObserver, useValidator } from '@/libs/validator';
import { useI18n } from 'vue-i18n';
import Button from '@ui-kit/controls/button/Button.vue';
import Checkbox from '@ui-kit/inputs/checkbox/Checkbox.vue';
import SingleLine from '@ui-kit/inputs/single-line/SingleLine.vue';
import MultiLine from '@ui-kit/inputs/multi-line/MultiLine.vue';
import ModalAddClient from './ModalAddClient.vue';
import ModalManageCarModel from '../../dictionary/car-models/modals/ModalManageCarModel.vue';
import ClientSelect from '../../../partials/client-select/ClientSelect.vue';
import { convertToNull } from '@/helpers/data-transform';
import CarSelect from '../../../partials/car-select/CarSelect.vue';

interface Props {
  data?: CarResource;
  client?: ClientResource;
}
interface Emits {
  (type: 'save', value: CarResource): void;
}
interface EngineFuelOption {
  name: string;
  id: EngineFuel;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'ModalManageClientCar',
  props: {
    data: null,
    client: null
  },
  emits: ["save"],
  setup(__props: any, { emit }: { emit: ({
  (type: 'save', value: CarResource): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  data?: CarResource;
  client?: ClientResource;
}

const { close } = useModalControls();
const emitter = useEmitter();
const carService = new CarService();
const carModelsStore = useCarModelsStore();
const modelsStore = useModalsStore();
const { t } = useI18n();





const pending = ref<boolean>(false);
const formData = ref<Partial<CarCreateUpdateDto>>({
  client_id: props.client?.id || props.data?.client?.id || undefined,
  car_model_id: props.data?.car_model.id || undefined,
  registration_plate: props.data?.registration_plate || '',
  vin_code: props.data?.vin_code || '',
  engine_displacement: props.data?.engine_displacement || undefined,
  engine_fuel: props.data?.engine_fuel || EngineFuel.petrol,
  lpg: props.data?.lpg || false,
  files: props.data?.files || [],
  manufacture_date: props.data?.manufacture_date || '',
  notes: props.data?.notes || '',
});
const serverData = computed<Partial<CarCreateUpdateDto>>(() => {
  return convertToNull(formData.value);
});

const validator = useValidator({
  data: serverData,
  schema: computed(() => carCreateUpdateSchema),
});

const selectedClient = ref<ClientResource | undefined>(props.client || props.data?.client || undefined);
watch(selectedClient, (value) => {
  formData.value.client_id = value?.id || undefined;
});

const selectedCarModel = computed<CarModelResource | undefined>(() => {
  return formData.value.car_model_id ? carModelsStore.data.find(({id}) => formData.value.car_model_id === id) : undefined;
});
function chooseCarModel(carModel: CarModelResource) {
  formData.value.car_model_id = carModel.id;
}

const engineFuels: EngineFuelOption[] = EngineFuelList.map((fuel) => ({
  id: fuel,
  name: t(`common.engine-fuel.${EngineFuelNameMap[fuel]}`),
}));
const selectedEngineFuel = computed<EngineFuelOption | undefined>(() => {
  return formData.value.engine_fuel ? engineFuels.find(({id}) => id === formData.value.engine_fuel) : undefined;
});
function chooseEngineFuel(fuel: EngineFuelOption) {
  formData.value.engine_fuel = fuel.id;
}
const engineFuelSelectDataManager = useSelectClientDataManager(({
  data: computed(() => engineFuels),
  selected: selectedEngineFuel,
  hideSelected: true,
}));

function addClient() {
  modelsStore.addModal({
    component: ModalAddClient,
    events: {
      save(client: ClientResource) {
        selectedClient.value = client;
      },
    },
  });
}
function addCarModel() {
  modelsStore.addModal({
    component: ModalManageCarModel,
    events: {
      save(carModel: CarModelResource) {
        formData.value.car_model_id = carModel.id;
      },
    },
  });
}

function save() {
  if (validator.validate()) {
    function successCallback(car: CarResource) {
      emit('save', car);
      if (props.data?.id) {
        emitter.emit('updateCar', car);
      } else {
        emitter.emit('addCar', car);
      }
      close();
    }

    pending.value = true;
    if (props.data?.id) {
      carService
        .update(props.data.id, serverData.value as CarCreateUpdateDto)
        .useValidator(validator)
        .send()
        .then((response) => {
          successCallback(response.data);
        })
        .finally(() => {
          pending.value = false;
        });
    } else {
      carService
        .add(serverData.value as CarCreateUpdateDto)
        .useValidator(validator)
        .send()
        .then((response) => {
          successCallback(response.data);
        })
        .finally(() => {
          pending.value = false;
        });
    }
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(BaseModal), {
    title: __props.data?.id ? _ctx.$t('pages.cars.edit-label') : _ctx.$t('pages.cars.create-label'),
    size: "xl",
    "no-body-padding": ""
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(ImageUpload, {
          modelValue: formData.value.files,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.value.files) = $event)),
          multiple: "",
          width: 500,
          ratio: [3, 2]
        }, null, 8, ["modelValue"]),
        _createElementVNode("div", _hoisted_2, [
          _createVNode(Label, {
            label: _ctx.$t('fields.client'),
            class: "flex-grow",
            required: ""
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(ValidationKeyObserver), { keys: "client_id" }, {
                default: _withCtx(({ firstErrorMessage }) => [
                  _createVNode(ClientSelect, {
                    modelValue: selectedClient.value,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectedClient).value = $event)),
                    "hide-empty": "",
                    disabled: !!__props.client,
                    errors: firstErrorMessage
                  }, null, 8, ["modelValue", "disabled", "errors"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["label"]),
          _createElementVNode("div", _hoisted_3, [
            _createVNode(Button, {
              disabled: !!__props.client,
              class: "w-48",
              "button-type": "outline",
              size: "xl",
              onClick: addClient
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('common.actions.new')), 1)
              ]),
              _: 1
            }, 8, ["disabled"])
          ])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(Label, {
            label: _ctx.$t('fields.car_model'),
            class: "flex-grow",
            required: ""
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(ValidationKeyObserver), { keys: "car_model_id" }, {
                default: _withCtx(({ firstErrorMessage }) => [
                  _createVNode(CarSelect, {
                    "model-value": _unref(selectedCarModel),
                    "onUpdate:modelValue": chooseCarModel,
                    errors: firstErrorMessage,
                    "hide-empty": ""
                  }, null, 8, ["model-value", "errors"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["label"]),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(Button, {
              class: "w-48",
              "button-type": "outline",
              size: "xl",
              onClick: addCarModel
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('common.actions.new')), 1)
              ]),
              _: 1
            })
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(Label, {
            label: _ctx.$t('fields.registration_plate'),
            class: "w-1/2"
          }, {
            default: _withCtx(({ id }) => [
              _createVNode(_unref(ValidationKeyObserver), { keys: "registration_plate" }, {
                default: _withCtx(({ firstErrorMessage }) => [
                  _createVNode(SingleLine, {
                    modelValue: formData.value.registration_plate,
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.registration_plate) = $event)),
                    id: id,
                    errors: firstErrorMessage
                  }, null, 8, ["modelValue", "id", "errors"])
                ]),
                _: 2
              }, 1024)
            ]),
            _: 1
          }, 8, ["label"]),
          _createVNode(Label, {
            label: _ctx.$t('fields.vin_code'),
            class: "w-1/2"
          }, {
            default: _withCtx(({ id }) => [
              _createVNode(_unref(ValidationKeyObserver), { keys: "vin_code" }, {
                default: _withCtx(({ firstErrorMessage }) => [
                  _createVNode(SingleLine, {
                    modelValue: formData.value.vin_code,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((formData.value.vin_code) = $event)),
                    id: id,
                    errors: firstErrorMessage
                  }, null, 8, ["modelValue", "id", "errors"])
                ]),
                _: 2
              }, 1024)
            ]),
            _: 1
          }, 8, ["label"])
        ]),
        _createElementVNode("div", _hoisted_7, [
          _createVNode(Label, {
            label: _ctx.$t('fields.engine_fuel'),
            class: "w-1/2"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(ValidationKeyObserver), { keys: "engine_fuel" }, {
                default: _withCtx(({ firstErrorMessage }) => [
                  _createVNode(_unref(SingleSelect), {
                    "data-manager": _unref(engineFuelSelectDataManager),
                    "model-value": _unref(selectedEngineFuel),
                    "onUpdate:modelValue": chooseEngineFuel,
                    "hide-empty": "",
                    "disable-search": "",
                    errors: firstErrorMessage
                  }, null, 8, ["data-manager", "model-value", "errors"])
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["label"]),
          _createVNode(Label, {
            label: _ctx.$t('fields.manufacture_date'),
            class: "w-1/2"
          }, {
            default: _withCtx(({ id }) => [
              _createVNode(_unref(ValidationKeyObserver), { keys: "manufacture_date" }, {
                default: _withCtx(({ firstErrorMessage }) => [
                  _createVNode(SingleLine, {
                    modelValue: formData.value.manufacture_date,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((formData.value.manufacture_date) = $event)),
                    id: id,
                    errors: firstErrorMessage
                  }, null, 8, ["modelValue", "id", "errors"])
                ]),
                _: 2
              }, 1024)
            ]),
            _: 1
          }, 8, ["label"])
        ]),
        _createElementVNode("div", _hoisted_8, [
          _createVNode(Label, {
            label: _ctx.$t('fields.engine_displacement'),
            class: "w-1/2"
          }, {
            default: _withCtx(({ id }) => [
              _createVNode(_unref(ValidationKeyObserver), { keys: "engine_displacement" }, {
                default: _withCtx(({ firstErrorMessage }) => [
                  _createVNode(SingleLine, {
                    modelValue: formData.value.engine_displacement,
                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((formData.value.engine_displacement) = $event)),
                    id: id,
                    type: "number",
                    errors: firstErrorMessage
                  }, null, 8, ["modelValue", "id", "errors"])
                ]),
                _: 2
              }, 1024)
            ]),
            _: 1
          }, 8, ["label"]),
          _hoisted_9
        ]),
        _createVNode(Checkbox, {
          label: _ctx.$t('fields.lpg'),
          modelValue: formData.value.lpg,
          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((formData.value.lpg) = $event))
        }, null, 8, ["label", "modelValue"]),
        _createVNode(Label, {
          label: _ctx.$t('fields.notes')
        }, {
          default: _withCtx(({ id }) => [
            _createVNode(_unref(ValidationKeyObserver), { keys: "notes" }, {
              default: _withCtx(({ firstErrorMessage }) => [
                _createVNode(MultiLine, {
                  modelValue: formData.value.notes,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((formData.value.notes) = $event)),
                  id: id,
                  errors: firstErrorMessage
                }, null, 8, ["modelValue", "id", "errors"])
              ]),
              _: 2
            }, 1024)
          ]),
          _: 1
        }, 8, ["label"]),
        _hoisted_10,
        _createElementVNode("div", _hoisted_11, [
          _createVNode(Button, {
            onClick: _unref(close),
            size: "lg",
            "button-type": "outline"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('common.actions.cancel')), 1)
            ]),
            _: 1
          }, 8, ["onClick"]),
          _createVNode(Button, {
            onClick: save,
            size: "lg"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('common.actions.save')), 1)
            ]),
            _: 1
          })
        ])
      ])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})